/**
 * Home rows (app-clarity §2.6, design-language §7): a title and one meta sentence in words, with at
 * most one glyph and no chips. Categories ("Product"), keycaps and "Suggested: …" stay in the
 * inspector; consequences read as words ("2 tasks wait on this").
 */
import { blockedReason, isBlockedRow } from './needs-you.js'
import type { InboxRow } from './now-inbox.js'

type MetaRow = Pick<
  InboxRow,
  | 'kind'
  | 'title'
  | 'reasons'
  | 'blocking'
  | 'unblocks'
  | 'risky'
  | 'projectName'
  | 'lane'
  | 'agentLabel'
  | 'chip'
>

/** The row's title: a blocked row names the work that stopped (its lane), else its reason. */
export function rowTitle(r: MetaRow): string {
  if (isBlockedRow(r as InboxRow)) return r.lane || blockedReason(r)
  return r.title
}

/** The meta words after the title, joined with " · " by the row. */
export function rowMeta(r: MetaRow): string[] {
  const out: string[] = []
  const blocked = isBlockedRow(r as InboxRow)
  if (blocked && r.lane) out.push(blockedReason(r))
  if (r.projectName) out.push(r.projectName)
  if (r.agentLabel) out.push(r.agentLabel)
  if (!blocked) {
    if (r.unblocks > 0)
      out.push(r.unblocks === 1 ? '1 task waits on this' : `${r.unblocks} tasks wait on this`)
    else if (r.blocking && r.kind === 'decision') out.push('the run waits')
    if (r.chip) out.push(r.chip)
    if (r.risky) out.push('needs a passkey')
  }
  return out
}

/** "42m", "3h", "2d": the age at the row's right edge (the full time is the tooltip). */
export function shortAgo(ts: number, now: number): string {
  const d = Math.max(0, now - ts)
  if (d < 45_000) return 'now'
  const m = Math.round(d / 60_000)
  if (m < 60) return `${m}m`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h`
  return `${Math.round(h / 24)}d`
}
